"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import posthog from "posthog-js";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiX } from "react-icons/fi";
import { HiOutlineDocumentText } from "react-icons/hi2";
import Button from "@/components/ui/button";
import { SearchSelect, SearchSelectContent, SearchSelectItem, SearchSelectTrigger } from "@/components/ui/select/search-select";
import { useUser } from "@/contexts/user-provider";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";
import { setCallScribeIntent } from "@/query/comms/chat";
interface UseCallScribeArgs {
  /** Backend Call id. Null until the call is correlated (outbound: from
   * /call/token; inbound: activeCallId after answer). */
  callId: string | null;
  /** Patient associated with the call. Null for an unmatched inbound caller. */
  patientId: string | null;
}
export interface CallScribe {
  canUseScribe: boolean;
  /** Whether the template-picker section should be shown. */
  sectionVisible: boolean;
  enabled: boolean;
  templateId: string | undefined;
  selectedTemplateName: string | undefined;
  templates: {
    id: string;
    name: string;
  }[];
  templatesLoading: boolean;
  isDisabled: boolean;
  disabledReason: string | undefined;
  /** True while a scribe-intent request is in flight. Callers disable hang-up
   * on this so the call can't end before the intent is persisted (which would
   * race the transcript webhook's intent lookup). */
  isSaving: boolean;
  /** Reveal the picker section (does not persist yet). */
  start: () => void;
  /** Hide the section and, if a note was enabled, turn it off. */
  stop: () => void;
  /** Choose a template — enables note generation for the call. */
  selectTemplate: (templateId: string) => void;
}

/**
 * State + persistence for the in-call "save visit as note" feature, shared
 * between the toggle button (in the call-controls row) and the surfaced
 * template-picker section (in the card body). Call once per card and pass the
 * returned object to <CallScribeButton> and <CallScribeSection>.
 *
 * Persistence is optimistic with rollback: a failed request reverts the
 * selection and surfaces an error toast, so the UI never implies a visit is
 * being captured when it isn't. Success is conveyed by the surfaced section /
 * active indicator rather than a toast.
 */
export function useCallScribe({
  callId,
  patientId
}: UseCallScribeArgs): CallScribe {
  const {
    currentUser
  } = useUser();
  const canUseScribe = hasPermission(currentUser, PERMISSIONS.AI_SCRIBE_USE);
  const isDisabled = !callId || !patientId;
  const disabledReason = !callId ? "Available once the call connects" : !patientId ? "Match a patient to save this call as a note" : undefined;

  // Fetch exactly when the picker becomes usable, so the query cannot disagree
  // with whether the UI can reach it. Fetching earlier bought nothing:
  // ActiveInboundCall is mounted from the console layout and stays mounted
  // between calls, which put a full template-list fetch on every console page
  // load for every scribe user.
  const {
    data: templates = [],
    isLoading: templatesLoading
  } = useQuery({
    ...noteQueryOptions.templates(),
    enabled: canUseScribe && !isDisabled
  });
  const [enabled, setEnabled] = useState(false);
  const [templateId, setTemplateId] = useState<string | undefined>(undefined);
  const [started, setStarted] = useState(false);
  // Count in-flight intent requests rather than a single boolean: overlapping
  // saves (e.g. changing the template twice quickly) must not let the first to
  // resolve clear the "saving" state while another is still pending — that
  // would re-enable hang-up mid-save.
  const [savingCount, setSavingCount] = useState(0);
  const isSaving = savingCount > 0;

  // Reset when the call changes so a prior call's "save as note" choice can't
  // leak into the next one. The inbound card stays mounted between calls (it
  // just renders null), so the hook persists across calls.
  useEffect(() => {
    setEnabled(false);
    setTemplateId(undefined);
    setStarted(false);
    setSavingCount(0);
  }, [callId]);
  const selectedTemplateName = useMemo(() => templates.find(t => t.id === templateId)?.name, [templates, templateId]);
  const persist = useCallback(async (nextEnabled: boolean, nextTemplateId: string | undefined) => {
    if (!callId) return;
    const prevEnabled = enabled;
    const prevTemplateId = templateId;

    // Optimistic update. The in-flight count holds the call open (hang-up
    // disabled) until every pending intent request settles, so the call can't
    // end before the transcript webhook would look the intent up.
    setEnabled(nextEnabled);
    setTemplateId(nextTemplateId);
    setSavingCount(c => c + 1);
    try {
      // Success is conveyed by the surfaced section + active indicator, so no
      // success toast here.
      await setCallScribeIntent(callId, nextEnabled, nextTemplateId ?? null);
      posthog.capture("call_scribe_intent_updated", {
        call_id: callId,
        enabled: nextEnabled,
        has_template: !!nextTemplateId
      });
    } catch {
      // Roll back so the UI never implies the visit is being captured when it
      // is not.
      setEnabled(prevEnabled);
      setTemplateId(prevTemplateId);
      toast({
        title: "Couldn't update note setting",
        description: "We couldn't save your choice for this call. Please try again.",
        variant: "error"
      });
    } finally {
      setSavingCount(c => c - 1);
    }
  }, [callId, enabled, templateId]);
  const start = useCallback(() => {
    if (isDisabled) return;
    setStarted(true);
  }, [isDisabled]);
  const stop = useCallback(() => {
    setStarted(false);
    if (enabled) {
      void persist(false, undefined);
    } else {
      setTemplateId(undefined);
    }
  }, [enabled, persist]);
  const selectTemplate = useCallback((id: string) => {
    void persist(true, id);
  }, [persist]);
  return {
    canUseScribe,
    sectionVisible: started || enabled,
    enabled,
    templateId,
    selectedTemplateName,
    templates,
    templatesLoading,
    isDisabled,
    disabledReason,
    isSaving,
    start,
    stop,
    selectTemplate
  };
}

/**
 * Toggle button for the call-controls row. Off → reveals the template-picker
 * section ("save visit as note"); on → hides it and turns capture off. There's
 * only the one action, so it's a plain toggle rather than a single-item menu.
 * The template choice itself lives in <CallScribeSection>.
 */
export const CallScribeButton: React.FC<{
  scribe: CallScribe;
}> = t0 => {
  const $ = _c(10);
  const {
    scribe
  } = t0;
  if (!scribe.canUseScribe) {
    return null;
  }
  const isActive = scribe.enabled && !!scribe.templateId;
  const t1 = scribe.sectionVisible ? scribe.stop : scribe.start;
  const t2 = scribe.isDisabled;
  const t3 = scribe.disabledReason ?? "Save visit as note";
  const t4 = scribe.sectionVisible;
  const t5 = scribe.sectionVisible && "bg-hover";
  const t6 = isActive && "bg-status-success-bg text-status-success-text hover:bg-status-success-bg/80";
  let t7;
  if ($[0] !== t5 || $[1] !== t6) {
    t7 = cx("relative p-2", t5, t6);
    $[0] = t5;
    $[1] = t6;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  let t8;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <HiOutlineDocumentText />;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  let t9;
  if ($[4] !== scribe.isDisabled || $[5] !== scribe.sectionVisible || $[6] !== t1 || $[7] !== t3 || $[8] !== t7) {
    t9 = <Button variant="primary" onClick={t1} disabled={t2} title={t3} aria-label="Save visit as note" aria-pressed={t4} className={t7}>{t8}</Button>;
    $[4] = scribe.isDisabled;
    $[5] = scribe.sectionVisible;
    $[6] = t1;
    $[7] = t3;
    $[8] = t7;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  return t9;
};

/**
 * Template-picker section surfaced in the card body once the provider chooses
 * "Save visit as note". Hidden by default so the select isn't present on every
 * call. Searchable, which matters when a tenant has many note templates.
 */
export const CallScribeSection: React.FC<{
  scribe: CallScribe;
}> = t0 => {
  const $ = _c(23);
  const {
    scribe
  } = t0;
  if (!scribe.canUseScribe || !scribe.sectionVisible) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="flex items-center gap-1.5 font-medium text-foreground"><HiOutlineDocumentText className="h-3.5 w-3.5 text-foreground-muted" aria-hidden={true} />Save visit as note</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FiX className="h-3.5 w-3.5" />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== scribe.stop) {
    t3 = <div className="flex items-center justify-between">{t1}<button type="button" onClick={scribe.stop} className="text-foreground-muted transition-colors hover:text-foreground" aria-label="Don't save this call">{t2}</button></div>;
    $[2] = scribe.stop;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = scribe.templateId;
  const t5 = scribe.selectTemplate;
  const t6 = scribe.selectedTemplateName ? undefined : "text-foreground-muted";
  const t7 = scribe.selectedTemplateName ?? (scribe.templatesLoading ? "Loading templates\u2026" : "Choose a note template");
  let t8;
  if ($[4] !== t6 || $[5] !== t7) {
    t8 = <SearchSelectTrigger className="min-h-8 text-caption" wrap={true}><span className={t6}>{t7}</span></SearchSelectTrigger>;
    $[4] = t6;
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== scribe.templates) {
    t9 = scribe.templates.map(_temp);
    $[7] = scribe.templates;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== t9) {
    t10 = <SearchSelectContent>{t9}</SearchSelectContent>;
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== scribe.selectTemplate || $[12] !== scribe.templateId || $[13] !== t10 || $[14] !== t8) {
    t11 = <SearchSelect value={t4} onValueChange={t5}>{t8}{t10}</SearchSelect>;
    $[11] = scribe.selectTemplate;
    $[12] = scribe.templateId;
    $[13] = t10;
    $[14] = t8;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = scribe.templateId ? "text-foreground-secondary" : "text-foreground-muted";
  const t13 = scribe.templateId ? "This call will be saved as a note." : "Pick a template to save this call as a note.";
  let t14;
  if ($[16] !== t12 || $[17] !== t13) {
    t14 = <p className={t12}>{t13}</p>;
    $[16] = t12;
    $[17] = t13;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== t11 || $[20] !== t14 || $[21] !== t3) {
    t15 = <div data-no-drag={true} className="space-y-2 rounded-lg border border-border/60 bg-background-secondary/30 px-3 py-2 text-caption">{t3}{t11}{t14}</div>;
    $[19] = t11;
    $[20] = t14;
    $[21] = t3;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  return t15;
};
function _temp(t) {
  return <SearchSelectItem key={t.id} value={t.id} wrap={true}>{t.name}</SearchSelectItem>;
}